import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Clear, OpenInNew, Search } from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import {
  GET_MATCHING_RUN_RESPONDENTS,
  MatchingRunRespondentsResult,
} from "../../Queries";
import { MatchingRespondent } from "../../types";

// ── Avatar helpers ─────────────────────────────────────────────────────────────

function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function nameInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return parts[0]?.[0]?.toUpperCase() ?? "?";
}

function RespondentAvatar({ name }: { name: string }) {
  return (
    <Avatar sx={{ bgcolor: stringToColor(name), width: 36, height: 36, fontSize: "0.85rem" }}>
      {nameInitials(name)}
    </Avatar>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────────

interface Props {
  runId: string;
  open: boolean;
  onClose: () => void;
  responseCount: number;
  tokenCount: number;
}

export function MatchingRespondentsModal({
  runId,
  open,
  onClose,
  responseCount,
  tokenCount,
}: Props) {
  const [search, setSearch] = useState("");

  const { data, loading } = useQuery<MatchingRunRespondentsResult>(
    GET_MATCHING_RUN_RESPONDENTS,
    { variables: { id: runId }, skip: !open },
  );

  const respondents: MatchingRespondent[] = data?.matchingRun?.respondents ?? [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return respondents;
    return respondents.filter(
      (r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    );
  }, [respondents, search]);

  const respondedCount = respondents.filter((r) => r.responded).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" component="span">
              Survey Responses
            </Typography>
            <Chip
              label={`${respondedCount} / ${tokenCount}`}
              size="small"
              color={respondedCount === tokenCount && tokenCount > 0 ? "success" : "default"}
            />
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Clear fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Search bar */}
        <Box px={2} py={1.5} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {/* List */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <List
            dense
            disablePadding
            sx={{ maxHeight: 420, overflowY: "auto" }}
          >
            {filtered.length === 0 && (
              <Box py={4} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  {search ? "No matches found." : "No students enrolled yet."}
                </Typography>
              </Box>
            )}
            {filtered.map((r) => (
              <ListItem
                key={r.userId}
                divider
                sx={{
                  "&:last-child": { borderBottom: "none" },
                  pr: 7,
                }}
              >
                <ListItemAvatar>
                  <RespondentAvatar name={r.name} />
                </ListItemAvatar>
                <ListItemText
                  primary={r.name}
                  secondary={r.email}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
                <Box display="flex" alignItems="center" gap={1} mr={1}>
                  <Chip
                    label={r.responded ? "Responded" : "Not responded"}
                    size="small"
                    color={r.responded ? "success" : "default"}
                    variant={r.responded ? "filled" : "outlined"}
                  />
                </Box>
                <ListItemSecondaryAction>
                  <Tooltip title="Open survey as this student">
                    <IconButton
                      size="small"
                      href={r.surveyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Footer summary */}
        {!loading && respondents.length > 0 && (
          <Box
            px={2}
            py={1}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="caption" color="text.secondary">
              {filtered.length < respondents.length
                ? `Showing ${filtered.length} of ${respondents.length} students`
                : `${respondents.length} student${respondents.length !== 1 ? "s" : ""} total`}
              {" · "}
              {responseCount} responded
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
